import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  AlertCircle,
  Hash,
  Info,
  KeyRound,
  RefreshCw,
  RotateCcw,
  Save,
  Check
} from 'lucide-react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Toast } from './ui/Toast';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import EnTeteSection from './parametrage/EnTeteSection';
import CarteReglages from './parametrage/CarteReglages';
import ChargementSection from './parametrage/ChargementSection';
import { api } from '../services/api';

/**
 * Paramètres des identifiants de connexion et des matricules d'élèves.
 *
 * ----------------------------------------------------------------------------
 * Pourquoi cette page
 * ----------------------------------------------------------------------------
 * Les deux étaient tirés au hasard, pareils pour toutes les écoles :
 * `rtchicaya364` et `MAT-2025-3993`. Le second a produit des doublons — deux
 * élèves différents sous le même matricule, 39 fois sur csap1.
 *
 * Désormais chaque école choisit :
 *   · la forme de ses identifiants, dans une liste — `prenom.nom` par défaut ;
 *   · le modèle de ses matricules, qu'elle écrit — `EPL-00` donne EPL-01,
 *     EPL-02… — numérotés à la suite par le serveur.
 *
 * Rien de ce qui existe ne change : un modèle ne s'applique qu'aux comptes et
 * aux élèves créés ensuite.
 *
 * Deux onglets, deux sections au régime B (PARAMETRAGE-MODULES.md §5) : chacun
 * s'enregistre séparément.
 */

const ACCENT_IDENTIFIANTS = '#246990';
const ACCENT_IDENTIFIANTS_SOMBRE = '#1a4f6e';
const ACCENT_MATRICULES = '#319190';
const ACCENT_MATRICULES_SOMBRE = '#2a7a79';

// Même règle que le serveur (NumerotationService.construireIdentifiant), pour
// montrer chaque modèle sans aller-retour. Le serveur reste seul à attribuer.
const lettres = (texte) => String(texte || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z]/g, '');

const CONSTRUCTIONS = {
  'prenom.nom': (p, n) => `${p}.${n}`,
  'nom.prenom': (p, n) => `${n}.${p}`,
  'p.nom': (p, n) => `${p.charAt(0)}.${n}`,
  pnom: (p, n) => `${p.charAt(0)}${n}`,
  prenom_nom: (p, n) => `${p}_${n}`,
  prenomnom: (p, n) => `${p}${n}`
};

const EXEMPLES = [
  { prenom: 'Rovely Doris', nom: 'TCHICAYA' },
  { prenom: 'Jean-Pierre', nom: 'MBEMBA NKOUNKOU' }
];

const exempleIdentifiant = (cle, { prenom, nom }) => {
  const p = lettres(String(prenom).trim().split(/\s+/)[0]);
  const n = lettres(nom);
  return (CONSTRUCTIONS[cle] || CONSTRUCTIONS['prenom.nom'])(p, n);
};

const BOUTON_REINITIALISER = 'flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-500 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40';
const boutonEnregistrer = (actif) => `flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all ${
  actif ? 'bg-[#246990] hover:shadow-md' : 'cursor-not-allowed bg-gray-300 shadow-none'
}`;

const Encadre = ({ titre, children }) => (
  <div className="rounded-xl border border-[#246990]/20 bg-[#eef7fb] p-4">
    <div className="flex gap-3">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[#246990]/20 bg-white shadow-sm">
        <Info className="h-4 w-4 text-[#246990]" />
      </div>
      <div className="text-xs leading-relaxed text-gray-600">
        <p className="mb-1 text-[13px] font-semibold text-[#246990]">{titre}</p>
        {children}
      </div>
    </div>
  </div>
);

const RappelNonEnregistre = ({ onEnregistrer, saving }) => (
  <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100">
      <AlertCircle size={15} className="text-amber-600" />
    </div>
    <p className="text-xs font-semibold text-amber-700">Vous avez des modifications non enregistrées.</p>
    <Button
      onClick={onEnregistrer}
      disabled={saving}
      className="ml-auto h-9 min-w-[168px] justify-center gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a]"
    >
      <Save className="h-4 w-4" />
      {saving ? 'Enregistrement…' : 'Enregistrer maintenant'}
    </Button>
  </div>
);

// ============================================================================
// Onglet Identifiants
// ============================================================================

const SectionIdentifiants = ({ etat, lectureSeule, onEnregistre, avertir }) => {
  const [modele, setModele] = useState(etat.modele_identifiant);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setModele(etat.modele_identifiant); }, [etat.modele_identifiant]);

  const hasChanges = modele !== etat.modele_identifiant;

  const enregistrer = async () => {
    setSaving(true);
    const reponse = await api.numerotation.enregistrer({ modele_identifiant: modele });
    setSaving(false);
    if (reponse?.success) {
      avertir('Modèle d\'identifiant enregistré', 'success');
      onEnregistre(reponse.data);
    } else {
      avertir(reponse?.message || "Erreur lors de l'enregistrement");
    }
  };

  return (
    <div className="space-y-5">
      <EnTeteSection
        icone={KeyRound}
        titre="Identifiants de connexion"
        sousTitre="La forme de l'identifiant donné à un employé quand son accès est ouvert"
        accent={ACCENT_IDENTIFIANTS}
        accentSombre={ACCENT_IDENTIFIANTS_SOMBRE}
        actions={
          <>
            <button
              type="button"
              onClick={() => setModele(etat.modele_identifiant)}
              disabled={saving || !hasChanges}
              className={BOUTON_REINITIALISER}
            >
              <RotateCcw size={13} /> Réinitialiser
            </button>
            <button
              type="button"
              onClick={enregistrer}
              disabled={saving || !hasChanges || lectureSeule}
              className={boutonEnregistrer(hasChanges && !lectureSeule)}
            >
              {saving
                ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                : <Save size={13} />}
              Enregistrer
            </button>
          </>
        }
      />

      <CarteReglages titre="Modèle" accent={ACCENT_IDENTIFIANTS} compteur={`${etat.modeles_identifiant.length} modèles`} dense>
        {etat.modeles_identifiant.map((m) => {
          const choisi = modele === m.cle;
          return (
            <button
              key={m.cle}
              type="button"
              onClick={() => setModele(m.cle)}
              disabled={lectureSeule}
              className={`flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-all ${
                choisi ? 'bg-white' : 'bg-gray-50/40'
              } hover:bg-gray-50/70 disabled:cursor-not-allowed`}
            >
              <div className="flex flex-1 items-center gap-3">
                <div
                  className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: choisi ? ACCENT_IDENTIFIANTS : '#9ca3af' }}
                />
                <div>
                  <p className={`font-mono text-sm font-semibold ${choisi ? 'text-gray-800' : 'text-gray-500'}`}>
                    {m.libelle}
                  </p>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-gray-400">
                    {EXEMPLES.map((ex) => `${ex.prenom} ${ex.nom} → ${exempleIdentifiant(m.cle, ex)}`).join('   ·   ')}
                  </p>
                </div>
              </div>
              <span
                className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border ${
                  choisi ? 'border-transparent text-white' : 'border-gray-300 bg-white'
                }`}
                style={choisi ? { backgroundColor: ACCENT_IDENTIFIANTS } : undefined}
              >
                {choisi && <Check size={12} />}
              </span>
            </button>
          );
        })}
      </CarteReglages>

      <Encadre titre="Ce qui change, et ce qui ne change pas">
        <p>
          Le modèle s'applique aux comptes ouverts ensuite. Les identifiants existants restent
          tels quels : leurs titulaires se connectent déjà avec. Seuls le premier prénom et le nom
          complet sont repris, sans accents, espaces ni tirets.
        </p>
        <p className="mt-1">
          Un homonyme reçoit un chiffre : <span className="font-mono">rovely.tchicaya2</span>. Deux
          comptes ne peuvent pas partager le même identifiant.
        </p>
      </Encadre>

      {hasChanges && !lectureSeule && <RappelNonEnregistre onEnregistrer={enregistrer} saving={saving} />}
    </div>
  );
};

// ============================================================================
// Onglet Matricules
// ============================================================================

const SectionMatricules = ({ etat, lectureSeule, onEnregistre, avertir }) => {
  const [modele, setModele] = useState(etat.modele_matricule);
  const [prochain, setProchain] = useState(etat.prochain_numero ? String(etat.prochain_numero) : '');
  const [apercu, setApercu] = useState(etat.apercu);
  const [calcul, setCalcul] = useState(false);
  const [saving, setSaving] = useState(false);
  const minuteur = useRef(null);

  useEffect(() => {
    setModele(etat.modele_matricule);
    setProchain(etat.prochain_numero ? String(etat.prochain_numero) : '');
    setApercu(etat.apercu);
  }, [etat]);

  const modeleChange = modele.trim() !== etat.modele_matricule;
  const prochainChange = prochain !== (etat.prochain_numero ? String(etat.prochain_numero) : '');
  const hasChanges = modeleChange || prochainChange;

  // L'aperçu est calculé par le serveur : lui seul connaît les numéros déjà
  // portés, que la numérotation saute.
  useEffect(() => {
    if (!hasChanges) {
      setApercu(etat.apercu);
      return undefined;
    }
    clearTimeout(minuteur.current);
    minuteur.current = setTimeout(async () => {
      setCalcul(true);
      const reponse = await api.numerotation.apercu({
        modele_matricule: modele.trim(),
        prochain_numero: prochain || undefined
      });
      setCalcul(false);
      if (reponse?.success) setApercu(reponse.data);
    }, 400);
    return () => clearTimeout(minuteur.current);
  }, [modele, prochain, hasChanges, etat.apercu]);

  const erreurModele = apercu?.erreurs?.modele_matricule || null;

  const reinitialiser = () => {
    setModele(etat.modele_matricule);
    setProchain(etat.prochain_numero ? String(etat.prochain_numero) : '');
  };

  const enregistrer = async () => {
    if (erreurModele) {
      avertir(erreurModele);
      return;
    }
    setSaving(true);
    const reponse = await api.numerotation.enregistrer({
      modele_matricule: modele.trim(),
      prochain_numero: prochainChange && prochain ? Number(prochain) : undefined
    });
    setSaving(false);
    if (reponse?.success) {
      avertir('Modèle de matricule enregistré', 'success');
      onEnregistre(reponse.data);
    } else {
      avertir(reponse?.message || "Erreur lors de l'enregistrement");
    }
  };

  return (
    <div className="space-y-5">
      <EnTeteSection
        icone={Hash}
        titre="Matricules des élèves"
        sousTitre="Le modèle du matricule attribué à chaque nouvel élève, et le numéro de départ"
        accent={ACCENT_MATRICULES}
        accentSombre={ACCENT_MATRICULES_SOMBRE}
        actions={
          <>
            <button type="button" onClick={reinitialiser} disabled={saving || !hasChanges} className={BOUTON_REINITIALISER}>
              <RotateCcw size={13} /> Réinitialiser
            </button>
            <button
              type="button"
              onClick={enregistrer}
              disabled={saving || !hasChanges || lectureSeule || Boolean(erreurModele)}
              className={boutonEnregistrer(hasChanges && !lectureSeule && !erreurModele)}
            >
              {saving
                ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                : <Save size={13} />}
              Enregistrer
            </button>
          </>
        }
      />

      <CarteReglages titre="Modèle" accent={ACCENT_MATRICULES} dense>
        <div className="flex flex-wrap items-start justify-between gap-6 px-5 py-4">
          <div className="max-w-xl">
            <p className="text-sm font-semibold text-gray-800">Modèle du matricule</p>
            <p className="mt-1.5 text-[11px] leading-relaxed text-gray-400">
              Le dernier groupe de <span className="font-mono">0</span> porte le numéro et fixe sa
              largeur : <span className="font-mono">EPL-00</span> donne EPL-01, EPL-02… puis EPL-100.
              <span className="font-mono"> {'{AAAA}'}</span> ou <span className="font-mono">{'{AA}'}</span> insère
              l'année ; la numérotation repart alors à 1 chaque année.
            </p>
          </div>
          <div className="w-64 flex-shrink-0">
            <input
              type="text"
              value={modele}
              onChange={(e) => setModele(e.target.value.toUpperCase().replace(/\s/g, '').replace('{AAAA}'.toUpperCase(), '{AAAA}'))}
              disabled={lectureSeule}
              maxLength={30}
              spellCheck="false"
              className={`h-9 w-full rounded-lg border px-3 font-mono text-sm focus:outline-none focus:ring-2 ${
                erreurModele
                  ? 'border-red-300 bg-red-50 text-red-900 focus:ring-red-200'
                  : 'border-gray-200 focus:border-[#246990] focus:ring-[#246990]/15'
              }`}
            />
            {erreurModele && <p className="mt-1.5 text-[11px] leading-relaxed text-red-700">{erreurModele}</p>}
          </div>
        </div>

        <div className="flex flex-wrap items-start justify-between gap-6 px-5 py-4">
          <div className="max-w-xl">
            <p className="text-sm font-semibold text-gray-800">Prochain numéro</p>
            <p className="mt-1.5 text-[11px] leading-relaxed text-gray-400">
              Pour reprendre la suite d'un registre papier. Tant que le modèle ne change pas, il ne
              peut que monter. Un numéro déjà porté par un élève est sauté.
            </p>
          </div>
          <input
            type="number"
            min="1"
            value={prochain}
            onChange={(e) => setProchain(e.target.value.replace(/[^\d]/g, ''))}
            disabled={lectureSeule}
            className="h-9 w-32 flex-shrink-0 rounded-lg border border-gray-200 text-center text-sm focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/15"
          />
        </div>
      </CarteReglages>

      <CarteReglages
        titre="Aperçu"
        accent={ACCENT_MATRICULES}
        compteur={apercu?.matricule ? `Année ${apercu.matricule.annee}` : undefined}
      >
        {apercu?.matricule && !erreurModele ? (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              {hasChanges ? 'Les trois prochains élèves recevraient :' : 'Les trois prochains élèves recevront :'}
              {calcul && <span className="ml-2 text-gray-400">calcul…</span>}
            </p>
            <div className="flex flex-wrap gap-2">
              {apercu.matricule.suivants.map((m) => (
                <span key={m} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 font-mono text-sm text-gray-800">
                  {m}
                </span>
              ))}
            </div>
            {etat.dernier_matricule && (
              <p className="text-[11px] text-gray-400">
                Dernier matricule attribué : <span className="font-mono">{etat.dernier_matricule.matricule}</span>
                {etat.dernier_matricule.eleve ? ` — ${etat.dernier_matricule.eleve}` : ''}
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-400">Corrigez le modèle pour voir l'aperçu.</p>
        )}
      </CarteReglages>

      <Encadre titre="Ce qui change, et ce qui ne change pas">
        <p>
          Le matricule est attribué par le serveur au moment où l'inscription est enregistrée,
          numéroté à la suite : deux élèves ne peuvent plus recevoir le même. Un élève réinscrit ou
          transféré garde le sien.
        </p>
        <p className="mt-1">
          Changer de modèle ne renumérote personne : les matricules existants restent tels quels.
        </p>
      </Encadre>

      {hasChanges && !lectureSeule && <RappelNonEnregistre onEnregistrer={enregistrer} saving={saving} />}
    </div>
  );
};

// ============================================================================
// La page
// ============================================================================

const ParametresNumerotation = ({ onBackToDashboard }) => {
  const [etat, setEtat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const avertir = useCallback((message, type = 'error') => setToast({ show: true, message, type }), []);

  const charger = useCallback(async () => {
    setLoading(true);
    const reponse = await api.numerotation.lire();
    if (reponse?.success) {
      setEtat(reponse.data);
      setErreur(null);
    } else {
      setErreur(reponse?.message || 'Lecture des réglages impossible');
    }
    setLoading(false);
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const lectureSeule = useMemo(() => Boolean(etat?.migrationManquante), [etat]);

  const enTete = (
    <EnTeteParametrage
      onRetour={onBackToDashboard}
      titre="Paramètres des identifiants et matricules"
      sousTitre="Forme des identifiants de connexion, modèle et numérotation des matricules"
    />
  );

  return (
    <div className="w-full">
      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      )}

      {enTete}

      {loading && !etat && (
        <div className="rounded-xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
          <ChargementSection accent={ACCENT_IDENTIFIANTS} libelle="Chargement des réglages…" />
        </div>
      )}

      {!loading && erreur && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-5">
          <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-700" />
          <div className="min-w-0 text-sm text-red-800">
            <p className="font-semibold text-red-900">Les réglages n'ont pas pu être lus</p>
            <p className="mt-1">{erreur}</p>
            <Button
              variant="outline"
              onClick={charger}
              className="mt-3 h-9 gap-2 border-red-300 text-red-800 hover:bg-red-100"
            >
              <RefreshCw size={15} /> Réessayer
            </Button>
          </div>
        </div>
      )}

      {etat && !erreur && (
        <>
          {lectureSeule && (
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-amber-600" />
              <p>
                Cette école n'a pas encore reçu la mise à jour des modèles de numérotation : les modèles
                par défaut s'appliquent et ne peuvent pas être changés. Contactez l'équipe technique.
              </p>
            </div>
          )}

          <Tabs defaultValue="identifiants" className="w-full">
            <TabsList className="mb-6 grid grid-cols-2 rounded-xl bg-[#246990]/8 p-1">
              <TabsTrigger
                value="identifiants"
                className="rounded-lg text-sm font-semibold text-gray-600 transition-all data-[state=active]:bg-[#246990] data-[state=active]:text-white data-[state=active]:shadow-sm"
              >
                <KeyRound size={15} className="mr-2" />
                <span>Identifiants</span>
              </TabsTrigger>
              <TabsTrigger
                value="matricules"
                className="rounded-lg text-sm font-semibold text-gray-600 transition-all data-[state=active]:bg-[#319190] data-[state=active]:text-white data-[state=active]:shadow-sm"
              >
                <Hash size={15} className="mr-2" />
                <span>Matricules</span>
              </TabsTrigger>
            </TabsList>

            <div className="rounded-xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
              <TabsContent value="identifiants" className="mt-0 focus-visible:outline-none">
                <SectionIdentifiants etat={etat} lectureSeule={lectureSeule} onEnregistre={setEtat} avertir={avertir} />
              </TabsContent>
              <TabsContent value="matricules" className="mt-0 focus-visible:outline-none">
                <SectionMatricules etat={etat} lectureSeule={lectureSeule} onEnregistre={setEtat} avertir={avertir} />
              </TabsContent>
            </div>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default ParametresNumerotation;
